import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import EngineeringIcon from "@mui/icons-material/Engineering";
import Box from "@mui/material/Box";
import { useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import { useRoles } from "../../providers/ContractProvider";
import theme from "../../theme";
import Unauthenticated from "../Layout/UnAuthenticated";
import { useAccount } from "@gu-corp/gu-wallet-connector";
import {
  useMobileSidebarState,
  useMobileSidebarVisibleState,
} from "../../providers/mobile-sidebar/MobileSidebarProvider";
import useAuthorized from "../../hooks/useAuthorized";
import { Link, Outlet, useLocation } from "react-router-dom";
import Authenticating from "../Layout/Authenticating";

const DRAWER_WIDTH = 240;
const DRAWER_MEDIUM_WIDTH = 200;

const LINKS = [
  { text: "Operators", href: "/manager/operators", icon: EngineeringIcon },
];

export default function ManagerLayout() {
  const location = useLocation();
  const { MANAGER_ROLE } = useRoles();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { account } = useAccount();
  const { authorized, loading } = useAuthorized(MANAGER_ROLE, account);

  const { setVisible } = useMobileSidebarVisibleState();
  useEffect(() => {
    if (authorized) {
      setVisible(true);
    }
  }, [authorized, setVisible]);
  const { open: mobileOpen } = useMobileSidebarState();

  if (loading) {
    return <Authenticating />;
  }

  if (!authorized) {
    return <Unauthenticated />;
  }

  return (
    <>
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            top: ["48px", "56px", "64px"],
            height: "auto",
            bottom: 0,
          },
          display: { xs: "block", sm: "none" },
        }}
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Divider />
        <List>
          {LINKS.map(({ text, href, icon: Icon }) => (
            <ListItem key={href} disablePadding>
              <ListItemButton
                component={Link}
                to={href}
                selected={href == location.pathname}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ mt: "auto" }} />
      </Drawer>
      <Drawer
        sx={{
          width: isMediumScreen ? DRAWER_MEDIUM_WIDTH : DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isMediumScreen ? DRAWER_MEDIUM_WIDTH : DRAWER_WIDTH,
            boxSizing: "border-box",
            top: ["48px", "56px", "64px"],
            height: "auto",
            bottom: 0,
          },
          display: { xs: "none", sm: "block" },
        }}
        variant="permanent"
        anchor="left"
        open={mobileOpen}
      >
        <Divider />
        <List>
          {LINKS.map(({ text, href, icon: Icon }) => (
            <ListItem key={href} disablePadding>
              <ListItemButton
                component={Link}
                to={href}
                selected={href == location.pathname}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ mt: "auto" }} />
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          ml: `${
            isSmallScreen
              ? 0
              : isMediumScreen
              ? DRAWER_MEDIUM_WIDTH
              : DRAWER_WIDTH
          }px`,
          p: [3],
        }}
      >
        <Outlet />
      </Box>
    </>
  );
}
